import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInquiryById,
  updateInquiryStatus,
  deleteInquiry,
  clearCurrentInquiry,
} from "../../store/slices/contactSlice";
import {
  FaArrowLeft,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaBuilding,
  FaCalendarAlt,
  FaTrash,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaTag,
} from "react-icons/fa";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  { value: "read", label: "Read", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "in-progress", label: "In Progress", color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { value: "responded", label: "Responded", color: "bg-green-100 text-green-800 border-green-200" },
  { value: "closed", label: "Closed", color: "bg-gray-100 text-gray-800 border-gray-200" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "text-green-600" },
  { value: "medium", label: "Medium", color: "text-yellow-600" },
  { value: "high", label: "High", color: "text-red-600" },
];

const AdminInquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentInquiry: inquiry, loading, error } = useSelector((s) => s.contact);

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    dispatch(fetchInquiryById(id));
    return () => dispatch(clearCurrentInquiry());
  }, [dispatch, id]);

  useEffect(() => {
    if (inquiry) {
      setStatus(inquiry.status || "new");
      setPriority(inquiry.priority || "medium");
    }
  }, [inquiry]);

  const handleStatusSave = async () => {
    setSaving(true);
    await dispatch(updateInquiryStatus({ id, status, priority }));
    setSaving(false);
  };

  const handleDelete = async () => {
    await dispatch(deleteInquiry(id));
    navigate("/admin/inquiries");
  };

  const getStatusBadge = (s) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === s);
    return opt
      ? `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${opt.color}`
      : "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700";
  };

  if (loading && !inquiry) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-10 w-10 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !inquiry) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl">
        {error}
      </div>
    );
  }

  if (!inquiry) return null;

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Back + title */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/inquiries")}
          className="p-2 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition"
        >
          <FaArrowLeft />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Inquiry Detail</h1>
          <p className="text-gray-400 text-xs mt-0.5">
            Received {new Date(inquiry.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="ml-auto">
          <span className={getStatusBadge(inquiry.status)}>
            {STATUS_OPTIONS.find((o) => o.value === inquiry.status)?.label || inquiry.status}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* ── Left: message ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Contact info card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Contact Information
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaUser className="text-blue-600 text-xs" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Name</p>
                  <p className="font-semibold text-gray-900">{inquiry.name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaEnvelope className="text-blue-600 text-xs" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <a
                    href={`mailto:${inquiry.email}`}
                    className="font-semibold text-blue-600 hover:underline break-all"
                  >
                    {inquiry.email}
                  </a>
                </div>
              </div>

              {inquiry.phone && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaPhone className="text-blue-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Phone</p>
                    <a href={`tel:${inquiry.phone}`} className="font-semibold text-gray-900">
                      {inquiry.phone}
                    </a>
                  </div>
                </div>
              )}

              {inquiry.company && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaBuilding className="text-blue-600 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Company</p>
                    <p className="font-semibold text-gray-900">{inquiry.company}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaCalendarAlt className="text-blue-600 text-xs" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Date</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(inquiry.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaTag className="text-blue-600 text-xs" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Priority</p>
                  <p className={`font-semibold capitalize ${PRIORITY_OPTIONS.find(p => p.value === inquiry.priority)?.color || "text-gray-700"}`}>
                    {inquiry.priority || "Medium"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subject + Message */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Subject
            </h2>
            <p className="font-semibold text-gray-900 text-lg mb-5">{inquiry.subject}</p>

            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Message
            </h2>
            <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
              {inquiry.message}
            </div>
          </div>

          {/* Response (if exists) */}
          {inquiry.response && (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-6">
              <h2 className="text-sm font-semibold text-green-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FaCheckCircle />
                Response Sent
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                {inquiry.response}
              </p>
              {inquiry.respondedAt && (
                <p className="text-green-600 text-xs mt-3">
                  Responded on {new Date(inquiry.respondedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Right: actions ── */}
        <div className="space-y-4">
          {/* Status update */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Update Status</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleStatusSave}
                disabled={saving || (status === inquiry.status && priority === inquiry.priority)}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold text-sm py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : null}
                Save Changes
              </button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <a
                href={`mailto:${inquiry.email}?subject=Re: ${encodeURIComponent(inquiry.subject)}`}
                className="flex items-center gap-2 w-full bg-gray-50 hover:bg-blue-50 hover:text-blue-700 text-gray-700 font-medium text-sm py-2.5 px-3 rounded-xl transition"
              >
                <FaEnvelope className="flex-shrink-0" />
                Reply via Email
              </a>

              {inquiry.phone && (
                <a
                  href={`tel:${inquiry.phone}`}
                  className="flex items-center gap-2 w-full bg-gray-50 hover:bg-green-50 hover:text-green-700 text-gray-700 font-medium text-sm py-2.5 px-3 rounded-xl transition"
                >
                  <FaPhone className="flex-shrink-0" />
                  Call
                </a>
              )}

              <button
                onClick={() => setDeleteConfirm(true)}
                className="flex items-center gap-2 w-full bg-gray-50 hover:bg-red-50 hover:text-red-600 text-gray-700 font-medium text-sm py-2.5 px-3 rounded-xl transition"
              >
                <FaTrash className="flex-shrink-0" />
                Delete Inquiry
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaTrash className="text-red-600 text-xl" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Delete Inquiry?</h3>
            <p className="text-gray-500 text-sm mb-6">
              This will permanently remove the inquiry from the system.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="flex-1 border border-gray-200 text-gray-700 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm py-2.5 rounded-xl transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiryDetail;
